import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';

const TAB_KEYS = ['general', 'metrics', 'receivables'];

export default function useVerificationConfigPageView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [editingIds, setEditingIds] = useState<string[]>([]);
    const [showValidationPanel, setShowValidationPanel] = useState(false);
    const [batchProcess, setBatchProcess] = useState<Record<string, { received: string; status: string; observation: string }>>({});
    const [batchStatus, setBatchStatus] = useState('');
    const [batchObservation, setBatchObservation] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [sortField, setSortField] = useState('titleNumber');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

    const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'general'));
    const setTabIndex = (idx: number) => setSearchParams({ tab: TAB_KEYS[idx] });

    useEffect(() => {
        if (!id) return;
        refresh();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = () => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getVerificationConfigView(id)
            .then(setData)
            .catch((e: any) => {
                if (e instanceof ApiError) {
                    if (e.status === 403) return navigate('/denied');
                    if (e.status === 404) return navigate('/not-found');
                }
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    };

    const titles = useMemo(() => {
        const list = data?.titles || [];
        return [...list].sort((a: any, b: any) => {
            const va = a[sortField] ?? '';
            const vb = b[sortField] ?? '';
            const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
            return sortOrder === 'ASC' ? cmp : -cmp;
        });
    }, [data, sortField, sortOrder]);

    const enabledProcesses = useMemo(() => {
        if (!data?.validationConfig) return [];
        const config = data.validationConfig;
        const isEnabled = (v: unknown) => v && v !== 'DO_NOT_VALIDATE';
        const processes: string[] = [];
        if (isEnabled(config.validation_xml)) processes.push('XML');
        if (isEnabled(config.validation_contact)) processes.push('DRAWEE');
        if (isEnabled(config.validation_invoice_pdf)) processes.push('INVOICE_PDF');
        if (isEnabled(config.validation_purchase_contract)) processes.push('PURCHASE_CONTRACT');
        if (isEnabled(config.validation_receipt_stub)) processes.push('RECEIPT_STUB');
        if (isEnabled(config.validation_sefaz)) processes.push('SEFAZ');
        if (isEnabled(config.validation_accepted_bill)) processes.push('ACCEPTED_BILL');
        return processes;
    }, [data]);

    const summary = useMemo(() => {
        if (!data) return { totalAmount: 0, validatedAmount: 0, docsCompleteAmount: 0, docsIncompleteAmount: 0 };
        const totalAmount = data.totalAmount || 0;
        const validatedAmount = totalAmount * ((data.validatedPercentage || 0) / 100);
        const docsCompleteAmount = titles.filter((t: any) => t.status === 'OK').reduce((s: number, t: any) => s + t.amount, 0);
        const docsIncompleteAmount = totalAmount - docsCompleteAmount;
        return { totalAmount, validatedAmount, docsCompleteAmount, docsIncompleteAmount };
    }, [data, titles]);

    const prefillAndOpenPanel = (ids: string[]) => {
        const selected = titles.filter((t: any) => ids.includes(t.id));
        const MIXED = '__MIXED__';
        const prefilled: Record<string, { received: string; status: string; observation: string }> = {};
        for (const p of enabledProcesses) {
            if (p === 'DRAWEE') continue;
            const items = selected.map((t: any) => (t.processItems || []).find((pi: any) => pi.processType === p)).filter(Boolean);
            const allReceived = items.length > 0 && items.every((i: any) => i.received === true);
            const allNotReceived = items.length > 0 && items.every((i: any) => i.received === false);
            const statuses = items.map((i: any) => i.status).filter(Boolean);
            const allSameStatus = statuses.length > 0 && statuses.every((s: string) => s === statuses[0]);
            const observations = items.map((i: any) => i.observation || '');
            const allSameObs = items.length > 0 && observations.every((o: string) => o === observations[0]);
            prefilled[p] = {
                received: allReceived ? 'true' : allNotReceived ? 'false' : (items.length > 0 ? MIXED : ''),
                status: allSameStatus ? statuses[0] : (statuses.length > 0 ? MIXED : ''),
                observation: allSameObs ? observations[0] : MIXED,
            };
        }
        setBatchProcess(prefilled);
        const docStatuses = selected.map((t: any) => t.status).filter(Boolean);
        const allSameDoc = docStatuses.length > 0 && docStatuses.every((s: string) => s === docStatuses[0]);
        setBatchStatus(allSameDoc ? docStatuses[0] : (docStatuses.length > 0 ? MIXED : ''));
        const obs = selected.map((t: any) => t.observation || '');
        const allSameObs = selected.length > 0 && obs.every((o: string) => o === obs[0]);
        setBatchObservation(allSameObs ? obs[0] : MIXED);
        setShowValidationPanel(true);
    };

    const openBatchPanel = () => {
        setEditingIds([]);
        prefillAndOpenPanel(Array.from(selectedIds));
    };

    const openEditPanel = (itemId: string) => {
        setEditingIds([itemId]);
        prefillAndOpenPanel([itemId]);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveValidation = async (result: { checks: { criteria_type: string; received?: boolean; status?: string; observation?: string }[]; receivableStatus?: string; receivableObservation?: string }) => {
        if (!id) return;
        setIsSaving(true);
        try {
            const ids = editingIds.length > 0 ? editingIds : Array.from(selectedIds);
            if (result.checks.length > 0 || result.receivableStatus || result.receivableObservation) {
                await collateralApi.registerChecks(id, ids, result.checks, result.receivableStatus, result.receivableObservation);
            }
            const refreshed = await collateralApi.getVerificationConfigView(id);
            setData(refreshed);
            toast.success('Validação registrada com sucesso');
            setShowValidationPanel(false);
            setSelectedIds(new Set());
            setEditingIds([]);
        } catch {
            toast.error('Erro ao registrar validação');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        id, navigate, isLoading, data, tabIndex, setTabIndex,
        titles, enabledProcesses, summary,
        selectedIds, setSelectedIds, editingIds,
        showValidationPanel, setShowValidationPanel,
        batchProcess, setBatchProcess, batchStatus, setBatchStatus,
        batchObservation, setBatchObservation, isSaving,
        sortField, setSortField, sortOrder, setSortOrder,
        openBatchPanel, openEditPanel, toggleSelect, handleSaveValidation, refresh,
    };
}
